import { format, parseISO, startOfDay, endOfDay, differenceInMinutes, isWithinInterval, differenceInDays, differenceInMonths, differenceInYears, getHours, getMinutes } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime, formatInTimeZone } from 'date-fns-tz';
import { de } from 'date-fns/locale';

// Get current timezone
export const getCurrentTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Get timezone offset in minutes
export const getTimezoneOffset = () => {
  return new Date().getTimezoneOffset();
};

// Convert local time to UTC while preserving the intended local time
export const localTimeToUTC = (localTime, timezone = getCurrentTimezone()) => {
  try {
    return zonedTimeToUtc(localTime, timezone);
  } catch (error) {
    console.warn('Timezone conversion failed, using local time:', error);
    return localTime;
  }
};

// Convert UTC time to local timezone
export const utcToLocalTime = (utcTime, timezone = getCurrentTimezone()) => {
  try {
    return utcToZonedTime(utcTime, timezone);
  } catch (error) {
    console.warn('Timezone conversion failed, using UTC time:', error);
    return utcTime;
  }
};

// Format time for display with timezone awareness
export const formatTime = (date, timezone = getCurrentTimezone()) => {
  try {
    return formatInTimeZone(date, timezone, 'HH:mm');
  } catch (error) {
    console.warn('Timezone formatting failed, using local format:', error);
    return format(date, 'HH:mm');
  }
};

// Format date for display with timezone awareness
export const formatDate = (date, timezone = getCurrentTimezone()) => {
  try {
    return formatInTimeZone(date, timezone, 'EEEE, d. MMMM yyyy', { locale: de });
  } catch (error) {
    console.warn('Timezone formatting failed, using local format:', error);
    return format(date, 'EEEE, d. MMMM yyyy', { locale: de });
  }
};

// Format short date with timezone awareness
export const formatShortDate = (date, timezone = getCurrentTimezone()) => {
  try {
    return formatInTimeZone(date, timezone, 'dd.MM.yyyy');
  } catch (error) {
    console.warn('Timezone formatting failed, using local format:', error);
    return format(date, 'dd.MM.yyyy');
  }
};

// Get current time with timezone
export const getCurrentTime = (timezone = getCurrentTimezone()) => {
  const now = new Date();
  return {
    time: now,
    timezone: timezone,
    formatted: formatTime(now, timezone),
    offset: getTimezoneOffset()
  };
};

// Calculate age from birth date (timezone independent)
export const calculateAge = (birthDate) => {
  const birth = new Date(birthDate);
  const today = new Date();
  const days = differenceInDays(today, birth);
  
  if (days < 30) {
    return {
      days,
      display: `${days} Tag${days !== 1 ? 'e' : ''}`,
      type: 'days'
    };
  } else if (days < 365) {
    const months = differenceInMonths(today, birth);
    const remainingDays = days % 30;
    if (remainingDays === 0) {
      return {
        months,
        display: `${months} Monat${months !== 1 ? 'e' : ''}`,
        type: 'months'
      };
    }
    return {
      months,
      days: remainingDays,
      display: `${months} Monat${months !== 1 ? 'e' : ''} ${remainingDays} Tag${remainingDays !== 1 ? 'e' : ''}`,
      type: 'months'
    };
  } else {
    const years = differenceInYears(today, birth);
    const remainingMonths = differenceInMonths(today, birth) % 12;
    if (remainingMonths === 0) {
      return {
        years,
        display: `${years} Jahr${years !== 1 ? 'e' : ''}`,
        type: 'years'
      };
    }
    return {
      years,
      months: remainingMonths,
      display: `${years} Jahr${years !== 1 ? 'e' : ''} ${remainingMonths} Monat${remainingMonths !== 1 ? 'e' : ''}`,
      type: 'years'
    };
  }
};

// Calculate duration between two times in minutes (timezone aware)
export const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  // Handle overnight sleep (end time is next day)
  if (end < start) {
    end.setDate(end.getDate() + 1);
  }
  
  return differenceInMinutes(end, start);
};

// Format duration as hours and minutes
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} Min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
};

// Check if time is during day (6:00-18:00) or night (timezone aware)
export const isDayTime = (time, timezone = getCurrentTimezone()) => {
  try {
    const localTime = utcToLocalTime(time, timezone);
    const hour = localTime.getHours();
    return hour >= 6 && hour < 18;
  } catch (error) {
    console.warn('Timezone check failed, using local time:', error);
    const hour = time.getHours();
    return hour >= 6 && hour < 18;
  }
};

// Get day/night sleep statistics (timezone aware)
export const getDayNightStats = (entries, timezone = getCurrentTimezone()) => {
  let daySleep = 0;
  let nightSleep = 0;
  
  entries.forEach(entry => {
    if (entry.startTime && entry.endTime) {
      const start = new Date(entry.startTime);
      const end = new Date(entry.endTime);
      const duration = calculateDuration(start, end);
      
      // Determine if this is primarily day or night sleep (timezone aware)
      const startHour = isDayTime(start, timezone) ? 'day' : 'night';
      const endHour = isDayTime(end, timezone) ? 'day' : 'night';
      
      // If sleep starts during day or ends during day, consider it day sleep
      if (startHour === 'day' || endHour === 'day') {
        daySleep += duration;
      } else {
        nightSleep += duration;
      }
    }
  });
  
  return { daySleep, nightSleep };
};

// Get sleep statistics for a date range (timezone aware)
export const getSleepStats = (entries, timezone = getCurrentTimezone()) => {
  if (!entries || entries.length === 0) {
    return {
      totalSleep: 0,
      averageSleep: 0,
      daySleep: 0,
      nightSleep: 0,
      sleepSessions: 0
    };
  }
  
  let totalSleep = 0;
  let sleepSessions = 0;
  
  entries.forEach(entry => {
    if (entry.startTime && entry.endTime) {
      const duration = calculateDuration(entry.startTime, entry.endTime);
      totalSleep += duration;
      sleepSessions++;
    }
  });
  
  const { daySleep, nightSleep } = getDayNightStats(entries, timezone);
  const averageSleep = sleepSessions > 0 ? totalSleep / sleepSessions : 0;
  
  return {
    totalSleep,
    averageSleep,
    daySleep,
    nightSleep,
    sleepSessions
  };
};

// Analyze sleep patterns (timezone aware)
export const analyzeSleepPatterns = (entries, excludeSpecialEvents = true, timezone = getCurrentTimezone()) => {
  if (!entries || entries.length === 0) {
    return {
      bedtime: null,
      wakeTime: null,
      confidence: 0
    };
  }

  // Filter out special events if requested
  const filteredEntries = excludeSpecialEvents 
    ? entries.filter(entry => !entry.specialEvent)
    : entries;

  if (filteredEntries.length === 0) {
    return {
      bedtime: null,
      wakeTime: null,
      confidence: 0
    };
  }

  const bedtimes = [];
  const wakeTimes = [];

  filteredEntries.forEach(entry => {
    if (entry.startTime && entry.type === 'start') {
      const time = new Date(entry.startTime);
      // Convert to local timezone for pattern analysis
      const localTime = utcToLocalTime(time, timezone);
      bedtimes.push(localTime);
    }
    if (entry.endTime && entry.type === 'end') {
      const time = new Date(entry.endTime);
      // Convert to local timezone for pattern analysis
      const localTime = utcToLocalTime(time, timezone);
      wakeTimes.push(localTime);
    }
  });

  // Calculate average bedtime
  const averageBedtime = bedtimes.length > 0 
    ? bedtimes.reduce((sum, time) => sum + time.getTime(), 0) / bedtimes.length
    : null;

  // Calculate average wake time
  const averageWakeTime = wakeTimes.length > 0
    ? wakeTimes.reduce((sum, time) => sum + time.getTime(), 0) / wakeTimes.length
    : null;

  // Calculate confidence based on consistency
  const confidence = Math.min(100, Math.max(0, 
    (filteredEntries.length / Math.max(entries.length, 1)) * 100
  ));

  return {
    bedtime: averageBedtime ? new Date(averageBedtime) : null,
    wakeTime: averageWakeTime ? new Date(averageWakeTime) : null,
    confidence: Math.round(confidence),
    sampleSize: filteredEntries.length
  };
};

// Detect special events in comments (including timezone-related keywords)
export const detectSpecialEvent = (comment) => {
  if (!comment) return false;
  
  const specialKeywords = [
    'urlaub', 'reise', 'flug', 'zeitverschiebung', 'jetlag',
    'hochzeit', 'feier', 'party', 'ausflug', 'besuch',
    'krank', 'fieber', 'impfung', 'zahnung',
    'unruhig', 'schlecht', 'probleme', 'stress',
    'timezone', 'zeitzone', 'reisezeit', 'flugzeit'
  ];
  
  const lowerComment = comment.toLowerCase();
  return specialKeywords.some(keyword => lowerComment.includes(keyword));
};

// Group entries by date (timezone aware)
export const groupEntriesByDate = (entries, timezone = getCurrentTimezone()) => {
  const grouped = {};
  
  entries.forEach(entry => {
    const date = formatShortDate(new Date(entry.date), timezone);
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(entry);
  });
  
  return grouped;
};

// Get week range for a given date (timezone aware)
export const getWeekRange = (date, timezone = getCurrentTimezone()) => {
  const localDate = utcToLocalTime(date, timezone);
  const start = startOfDay(localDate);
  start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
  
  const end = endOfDay(new Date(start));
  end.setDate(end.getDate() + 6); // End of week (Saturday)
  
  return { start, end };
};

// Get month range for a given date (timezone aware)
export const getMonthRange = (date, timezone = getCurrentTimezone()) => {
  const localDate = utcToLocalTime(date, timezone);
  const start = new Date(localDate.getFullYear(), localDate.getMonth(), 1);
  const end = new Date(localDate.getFullYear(), localDate.getMonth() + 1, 0);
  
  return { start, end };
};

// Get time in minutes since midnight (timezone aware)
export const getMinutesSinceMidnight = (time, timezone = getCurrentTimezone()) => {
  try {
    const localTime = utcToLocalTime(time, timezone);
    return getHours(localTime) * 60 + getMinutes(localTime);
  } catch (error) {
    console.warn('Timezone conversion failed, using local time:', error);
    return getHours(time) * 60 + getMinutes(time);
  }
};

// Format time for pattern display (timezone aware)
export const formatPatternTime = (time, timezone = getCurrentTimezone()) => {
  if (!time) return '--:--';
  return formatTime(time, timezone);
};

// Get timezone information for display
export const getTimezoneInfo = () => {
  const timezone = getCurrentTimezone();
  const offset = getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const offsetSign = offset <= 0 ? '+' : '-';
  
  return {
    name: timezone,
    offset: offset,
    offsetFormatted: `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`,
    display: `${timezone} (UTC${offsetSign}${offsetHours}:${offsetMinutes.toString().padStart(2, '0')})`
  };
};

// Check if timezone has changed (for detecting travel)
export const hasTimezoneChanged = (previousTimezone, currentTimezone) => {
  return previousTimezone && currentTimezone && previousTimezone !== currentTimezone;
};

// Get timezone change information
export const getTimezoneChangeInfo = (oldTimezone, newTimezone) => {
  if (!oldTimezone || !newTimezone || oldTimezone === newTimezone) {
    return null;
  }
  
  try {
    const now = new Date();
    const oldTime = utcToZonedTime(now, oldTimezone);
    const newTime = utcToZonedTime(now, newTimezone);
    const diffHours = (newTime.getTime() - oldTime.getTime()) / (1000 * 60 * 60);
    
    return {
      oldTimezone,
      newTimezone,
      differenceHours: diffHours,
      direction: diffHours > 0 ? 'forward' : 'backward',
      display: `${diffHours > 0 ? '+' : ''}${Math.round(diffHours)} Stunden`
    };
  } catch (error) {
    console.warn('Could not calculate timezone difference:', error);
    return {
      oldTimezone,
      newTimezone,
      differenceHours: 0,
      direction: 'unknown',
      display: 'Zeitzone geändert'
    };
  }
}; 